import React, { useEffect, useRef, useState } from "react";
import { Lightbulb, Loader2 } from "lucide-react";
import api, { formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PaletteVariants } from "@/components/PaletteVariants";
import { PickCard, PickHint } from "@/components/PickCard";

/** ٣ مقترحات لوقو مصغّرة يختار منها العميل قبل التوليد النهائي — تُحفظ في design_inputs (concept_prompt/meaning/title) */
export const LogoConcepts = ({ brand, inputs, onPick, projectId = "" }) => {
  const [busy, setBusy] = useState(false);
  const name = (inputs?.brand_name || brand || "").trim();
  const storeKey = `rawaj_logo_concepts_${projectId}_${name.toLowerCase()}`;  // لكل مشروع واسم علامة مخزن مستقل — لا تتسرب مقترحات مشروع لآخر
  const [list, setList] = useState([]);
  const [note, setNote] = useState("");
  useEffect(() => {  // استعادة المقترحات السابقة لنفس العلامة عند العودة للصفحة
    if (!name) { setList([]); setNote(""); return; }
    try { const s = JSON.parse(localStorage.getItem(storeKey) || "null"); setList(s?.brand === name ? (s.concepts || []) : []); setNote(s?.brand === name ? (s.note || "") : ""); } catch { setList([]); }
  }, [storeKey]);
  const chosen = inputs?.concept_title || "";
  const fontKey = inputs?.wordmark_font || "bold";
  const fontRef = useRef(fontKey);
  useEffect(() => {  // تغيير الخط → إعادة كتابة الاسم على المقترح المختار بالخط الجديد فورًا
    if (fontRef.current === fontKey) return;
    fontRef.current = fontKey;
    const idx = list.findIndex((x) => x.title === chosen);
    if (idx >= 0 && list[idx].symbol) recolor(list[idx], idx, list[idx].palette || [], "", true);
  }, [fontKey]); // eslint-disable-line react-hooks/exhaustive-deps
  const [recoloring, setRecoloring] = useState(false);
  const [notice, setNotice] = useState("");
  const noticeTimer = useRef(null);
  const pick = (c, palette) => onPick({ concept_title: c.title, concept_prompt: c.prompt, concept_meaning: c.meaning, concept_style: c.style || "", concept_palette: (palette || c.palette || []).join(", "), concept_image: c.symbol || c.image || "" });
  const recolor = async (c, idx, palette, paletteName = "", fontOnly = false) => {
    if (!c.symbol && !c.image) return pick(c, palette);
    setRecoloring(true); setNotice(""); clearTimeout(noticeTimer.current);
    const t0 = Date.now();
    try {
      // المقترحات القديمة بلا رمز منفصل: نعيد تلوين الصورة كما هي دون كتابة الاسم مرة ثانية
      const { data } = await api.post("/content/recolor", { image: c.symbol || c.image, palette, brand_name: c.symbol ? name : "", tagline: c.symbol ? (inputs?.slogan || "") : "", wordmark_font: inputs?.wordmark_font || "bold" });
      await new Promise((r) => setTimeout(r, Math.max(0, 1200 - (Date.now() - t0))));  // يُرى مؤشر «جاري تغيير اللون» بوضوح
      const next = list.map((x, i) => (i === idx ? { ...x, image: data.image, symbol: c.symbol ? data.symbol : x.symbol, palette: data.palette, base_palette: x.base_palette || x.palette } : x));
      setList(next);
      try { localStorage.setItem(storeKey, JSON.stringify({ brand: name, concepts: next, note })); } catch {}
      pick(next[idx], data.palette);
      setNotice(fontOnly ? "تم تغيير خط الاسم حسب اختيارك" : `تم تلوين اللوقو بلوحة «${paletteName || "المختارة"}» — ${data.palette.join(" · ")}`);
      noticeTimer.current = setTimeout(() => setNotice(""), 5000);
    } catch (err) { toast.error(formatError(err.response?.data?.detail) || "تعذّر تغيير اللون — حاول مرة أخرى"); }
    finally { setRecoloring(false); }
  };
  const load = async (fresh) => {
    if (!name) return toast.error("اكتب اسم العلامة أولًا");
    setBusy(true);
    try {
      const { data } = await api.post("/content/logo-concepts", { project_id: projectId, brand_name: name, design_inputs: inputs || {}, fresh: !!fresh });
      const concepts = data.concepts || [];
      setList(concepts);
      setNote(data.note || "");
      if (concepts.length) { try { localStorage.setItem(storeKey, JSON.stringify({ brand: name, concepts, note: data.note || "" })); } catch {} }
      else toast.error("لم تصل مقترحات، حاول مرة أخرى");
      if (data.cached && fresh) toast.info(data.note || "هذه مقترحاتك السابقة");
    } catch (err) { toast.error(formatError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-dashed border-primary/40 bg-primary/5 p-3 space-y-2" data-testid="logo-concepts">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <p className="text-xs"><b className="text-primary">قبل التوليد:</b> اعرض ٣ مقترحات مصغّرة بألوانك واختر الأقرب لذوقك — التوليد النهائي يتبع المفهوم المختار حرفيًا. مقترحاتك تبقى محفوظة هنا حتى لو غادرت الصفحة.</p>
        <Button type="button" size="sm" variant="outline" onClick={() => load(list.length > 0)} disabled={busy} className="gap-1 border-primary/40 text-primary" data-testid="logo-concepts-btn">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lightbulb className="w-4 h-4" />} {list.length ? "مقترحات جديدة" : "اعرض ٣ مقترحات"}
        </Button>
      </div>
      {list.length > 0 && (
        <div className="grid sm:grid-cols-3 gap-2">
          {list.map((c, i) => {
            const active = chosen === c.title;
            return (
              <PickCard key={i} active={active} busy={recoloring} notice={notice} onClick={() => pick(c)} testid={`logo-concept-${i}`} image={c.image || ""} imageClass="aspect-square">
                {c.palette?.length > 0 && (
                  <div className="flex items-center gap-1 mb-1" data-testid={`concept-palette-${i}`}>
                    {c.palette.map((hx) => <span key={hx} title={hx} className="inline-block w-4 h-4 rounded-full border border-border" style={{ background: hx }} />)}
                    <span className="text-[10px] text-muted-foreground mr-1" dir="ltr">{c.palette.join(" ")}</span>
                  </div>
                )}
                <div className="text-xs font-bold flex items-center gap-1 flex-wrap">{c.title}{c.style_name && <span className="text-[9px] font-medium rounded-full bg-primary/10 border border-primary/30 px-1.5 py-px text-primary" data-testid={`concept-style-${i}`}>{c.style_name}</span>}{c.trend && <span className="text-[9px] font-medium rounded-full bg-secondary border border-border px-1.5 py-px text-muted-foreground" dir="ltr" data-testid={`concept-trend-${i}`}>{c.trend}</span>}</div>
                <p className="text-[11px] text-muted-foreground leading-relaxed mt-0.5">{c.meaning}</p>
              </PickCard>
            );
          })}
        </div>
      )}
      <PickHint show={list.length > 0 && !chosen} text="اختر مقترحًا واحدًا من الثلاثة بالضغط عليه — تظهر عليه علامة «المختار» ثم تظهر خيارات الألوان." testid="logo-concepts-pick-hint" />
      {note && list.length > 0 && <p className="text-[11px] text-muted-foreground">{note}</p>}
      {chosen && (() => {
        const idx = list.findIndex((c) => c.title === chosen);
        const c = idx >= 0 ? list[idx] : null;
        return (
          <div className="space-y-1.5">
            <p className="text-[11px] text-emerald-700 dark:text-emerald-400" data-testid="logo-concept-chosen">المفهوم المختار: <b>{chosen}</b> — التوليد النهائي يتبع هذا الرمز وألوانه حرفيًا، ويُكتب اسم «{name}» تحته بخط عربي صحيح مع وصف متقن في وثيقة الهوية.</p>
            {c && <PaletteVariants base={c.base_palette || c.palette || []} selected={c.palette || []} busy={recoloring} onPick={(cols, nm) => recolor(c, idx, cols, nm)} testid="logo-palette-variants" />}
          </div>
        );
      })()}
    </div>
  );
};
